<template>
  <div
    class="view-index"
    :class="!browerInfo.isMobile ? 'pc-only' : ''"
  >
    <div
      data-desc="for-aegis-fmp"
      AEGIS-FIRST-SCREEN-TIMING
    />
    <banner
      v-show="showBanner"
      v-if="bannerExist"
      :person="bannerData?.person ?? null"
      :banner-txt="bannerData?.bannerTxt ?? ''"
      :btn-txt="bannerData?.btnTxt ?? ''"
      :mini-app-path-click-avator="miniAppPathClickAvator"
      :mini-app-path-click-look="miniAppPathClickLook"
      :exposure-material-info="exposureMaterialInfo"
      :active-feed-report-opt="activeFeedReportOpt"
    />
    <swiper-container-wrap
      :is-show-drama-replay="isShowDramaReplay"
      @on-replay="onReplay"
    >
      <div
        ref="swiperContainerRef"
        class="swiper-container"
      >
        <div class="swiper-wrapper">
          <div
            v-for="(feed, index) in feedsList"
            :key="index"
            :data-swiper-slide-index="index"
            class="swiper-slide"
          >
            <swiper-item
              ref="swiperItemRef"
              :feed="feed"
              :is-last-feed="index === feedsList.length - 1"
              :init-play-rec="initPlayRec"
              :show-rec-in-play-page="showRecInPlayPage"
              :strategy-loaded="true"
              :play-icon-click="playIconClick"
              @on-ended="onEnded"
            />
          </div>
        </div>
      </div>
    </swiper-container-wrap>
    <div
      v-show="showHandTip"
      class="hand-overlay"
    >
      <canvas
        ref="handRef"
        class="hand-icon"
      />
    </div>
    <hot-drama-container
      v-if="isDrama"
      v-show="!isFullScreen"
    />
    <collection-container
      v-if="isCollection"
      :show-rec-in-play-page="showRecInPlayPage"
      :active-feed="activeFeed"
      :active-feed-report-opt="activeFeedReportOpt"
    />
    <error
      v-if="!!errorMsg"
      :msg="errorMsg"
    />
  </div>
  <div
    class="qrcode-wrap"
    :class="!browerInfo.isMobile ? 'pc-only' : ''"
  >
    <img
      class="qrcode"
      src="https://qzonestyle.gtimg.cn/qz-proj/weishi-pc/img/qrcode.png"
    >
    <p class="txt">
      下载客户端
    </p>
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import Swiper from 'swiper';
import { useStore, MutationTypes } from '@/store/index';
import SwiperItem from '@/components/swiper-item-new.vue';
import CollectionContainer from '@/components/collection-container.vue';
import HotDramaContainer from '@/components/hot-drama-container.vue';
import SwiperContainerWrap from '@/components/swiper-container-wrap.vue';
import Banner from '@/components/common/banner-new.vue';
import Error from '@/components/error.vue';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useProvideBrowserInfo from '@/composable/use-provide-browser-info';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import useProvideCookieInfo from '@/composable/use-provide-cookie-info';
import useInitializeHomeData from '@/composable/use-initialize-home-data';
import useInitializeShareData from '@/composable/use-initialize-share-data';
import useRequestDataMethods from '@/composable/use-request-data-method';
import useVideoEventHandler from '@/composable/use-video-event-handler';
import { EMiniAppPosition } from '@/constants/mini-app';
import { EFeedReserveType } from '@/constants/feed';
import { EVideoEvtNameTypes } from '@/constants/video';
import { getSwiper, getPAGLib } from '@/utils/script';
import { IS_SSR } from '@/utils/env';
import { openSchema } from '@/utils/schema';
import { report } from '@/libs/report';
import { initWXConf } from '@/utils/wx';
import 'swiper/swiper-bundle.css';

const store = useStore();
const { state } = store;

const { provideInfo: provideUrlInfo, info: urlInfo } = useProvideUrlInfo();
const { provideInfo: provideBrowserInfo, info: browerInfo } = useProvideBrowserInfo();
const { provideInfo: provideCookieInfo } = useProvideCookieInfo();
// 提供 utils
const {
  getMiniAppPath,
  getMiniappPlayerFeed,
  openSchemaAppendParamsClipboard,
  openCollectionSchema,
  getLogsour,
} = useGetCommonMethods();
// 拉取推荐 feed 数据
const { fetchRecFeeds } = useRequestDataMethods();
const { emitVideoEvent } = useVideoEventHandler();

const swiper = ref<Swiper>(); // swiper 对象，主要用于切换视频
const isWsInstalled = ref<boolean | null>(null); // 是否安装了微视 app  null 表示未知
const swiperContainerRef = ref<HTMLElement>();
const attachInfo = ref<string>('{ "page": 1, "total_num": 12 }');
const loadingEnd = ref<boolean>(false);
const loadingRecFeeds = ref<boolean>(false);
const isShowDramaReplay = ref<boolean>(false);
const isInteractive = ref<boolean>(urlInfo.query?.vtype === 'interactive');
const hasShowJump = ref<boolean>(false);
const handRef = ref<HTMLElement>();
const pageEntryTime = ref<number>(+new Date());

// 是否初始化 banner
const bannerExist = computed(() => {
  const { scene } = urlInfo.query;
  if (browerInfo.client === 'qq' || isInteractive.value) {
    // qq 场景默认不展示 banner
    return false;
  }
  // scene === 7|infoflow 的场景，属于投放页面情形，不显示 banner
  return !(scene === '7' || scene === 'infoflow');
});
const activeIndex = computed(() => state.activeIndex);
const isCollection = computed(() => state.isCollection);
const isDrama = computed(() => state.isDrama);
const growthTestData = computed(() => state.growthTestData);
const showCollection = computed(() => state.showCollection);
const feedsList = computed(() => state.feedsList);
const bannerData = computed(() => state.banner);
const errorMsg = computed(() => state.errorMsg);
const showHandTip = computed(() => state.showHandTip);
const isFullScreen = computed(() => state.isFullScreen);

const isNormal = computed(() => !isCollection.value && !isDrama.value);
const showRecInPlayPage = computed(() => !isCollection.value && !!growthTestData.value?.showModule);
const showBanner = computed(() => {
  if (isCollection.value) {
    // 精选集和 banner 是一起展示隐藏的
    return showCollection.value;
  }
  return true;
});
const miniAppPathClickAvator = computed(() => getMiniAppPath(EMiniAppPosition.videoHeadpic, {
  ownerid: bannerData.value?.person?.id || '',
}));
const miniAppPathClickLook = computed(() => getMiniAppPath(EMiniAppPosition.open));
const exposureMaterialInfo = computed(() => feedsList.value[activeIndex.value]?.reserve[
  EFeedReserveType.exposureMaterialInfo
]);
const activeFeed = computed(() => feedsList.value[activeIndex.value] || feedsList.value[0]);
const activeFeedReportOpt = computed(() => ({
  video_id: activeFeed.value?.video.fileId ?? '',
  owner_id: activeFeed.value?.poster.id ?? '',
}));

const initPlayRec = false;
const swiperOptions = {
  direction: 'vertical',
  resistanceRatio: 0,
  initialSlide: 0,
  allowTouchMove: !(isDrama.value || isCollection.value || isInteractive.value),
  on: {
    slideChange: (swiper: Swiper) => {
      if (isNormal.value && swiper.activeIndex >= feedsList.value.length - 2) { // 普通视频
        getRecFeeds();
      }
    },
    slideChangeTransitionEnd: (swiper: Swiper) => {
      store.commit(MutationTypes.SET_ACTIVE_INDEX, { value: swiper.activeIndex });
      if (isNormal.value) {
        emitVideoEvent({
          index: swiper.activeIndex,
          eventName: EVideoEvtNameTypes.play,
        });
        report('slide.move');

        if (!hasShowJump.value && swiper.activeIndex === 3) { // 普通视频第 4 个视频不自动播放，需要手动暂停上一个视频的播放，仅出现一次
          hasShowJump.value = true;
          autoJumpApp();
        }
      }
    },
  },
};

const initSwiper = async () => {
  const Swiper = await getSwiper();
  if (Swiper) {
    // @ts-ignore
    swiper.value = new Swiper(swiperContainerRef.value, {
      ...swiperOptions,
      initialSlide: activeIndex.value,
    });
  }
};

const initPagLib = async () => {
  const PAGLib = await getPAGLib();
  if (PAGLib) {
    const PAG = await PAGLib.PAGInit();
    const buffer = await fetch('//isee.weishi.qq.com/share/hand.pag').then((response) => response.arrayBuffer());
    const pagFile = await PAG.PAGFile.load(buffer);
    const canvas = handRef.value;
    // Create PAGView.
    const pagView = await PAG.PAGView.init(pagFile, canvas);
    // Set PAGView play infinity.
    pagView.setRepeatCount(0);
    await pagView.play();
  }
};

const studyModeTips = () => {
  window.mqq?.ui.showTips({
    text: '青少年模式下不可访问',
    iconMode: 2,
  });
};

// 最终会传递给video-player组件，点击播放及暂停按钮时调用
const playIconClick = () => {
  // 手Q 青少年模式下不可访问
  if (browerInfo.isQQStudyMode) {
    studyModeTips();
    return false;
  }
  return true;
};

const onEnded = () => {
  if (isDrama.value) { // 短剧播放结束后弹起重复播放按钮
    emitVideoEvent({
      index: activeIndex.value,
      eventName: EVideoEvtNameTypes.pause,
    });
    isShowDramaReplay.value = true;
  } else { // 非短剧播放结束后重复播放
    emitVideoEvent({
      index: activeIndex.value,
      eventName: EVideoEvtNameTypes.play,
    });
  }
};

const onReplay = () => {
  isShowDramaReplay.value = false;
  emitVideoEvent({
    index: activeIndex.value,
    eventName: EVideoEvtNameTypes.play,
  });
};

// 自动拉活
const autoJumpApp = async () => {
  const { id: feedId } = activeFeed.value;
  const logsour = getLogsour();
  openSchemaAppendParamsClipboard(true, `weishi://feed?feed_id=${feedId}&session_from=10000018${logsour}`);

  report('call.app.popup.exposure', {
    type: {
      play_status: isFullScreen.value ? 2 : 1,
      clk_position: 'auto',
    },
  });
};

// 微博自动拉活
const autoDownloadWSInWeibo = async () => {
  const { id: feedId } = activeFeed.value;
  if (browerInfo.client !== 'wb') {
    return;
  }
  if (isWsInstalled.value === false) {
    openSchemaAppendParamsClipboard(true, 'feed', {
      feed_id: feedId,
    });
  }
};

const openWsInQQ = async () => {
  const {
    id: feedId,
    collectionid: collectionId,
    spid: sharePersonId,
  } = urlInfo.query;
  const feed = feedsList.value[activeIndex.value];
  if (!feed || browerInfo.client !== 'qq') return;
  if (!window) return;
  const { isQQStudyMode } = browerInfo;
  // 启用青少年模式,弹窗提示,并禁止视频的播放,不再拉起微视公众号
  if (isQQStudyMode) {
    studyModeTips();
    return;
  }

  const params = getMiniappPlayerFeed(feed);
  if (params?.videoUrl) {
    const isOwner = feed.posterId === sharePersonId;
    const webTestId = growthTestData.value?.strategyID || '';
    const spid = sharePersonId;
    const scene = isOwner ? 'homepage_main' : 'homepage_guest';
    window?.mqq?.invokeSchema('mqqapi', 'wsgzh', 'miniapp_player', {
      feeds: encodeURIComponent(JSON.stringify(params)),
      scene,
      scenes_from: 'share', // 场景
      action: 'openPlayer', // 行为
      spid,
      collection_id: collectionId || '',
      active_id: '',
      share_scenes_from: 11,
      web_test_id: webTestId,
    });
    window.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        window?.mqq?.invoke('ui', 'popBack');
      }
    });
    return;
  }

  if (isCollection.value) {
    // 跳转到中间页去下载或唤起微视
    openCollectionSchema();
  } else {
    // 直接唤起微视
    openSchema('feed', { feed_id: feedId });
  }
};

const getRecFeeds = async () => {
  if (loadingRecFeeds.value) {
    return;
  }

  loadingRecFeeds.value = true;
  // 一次拉取的个数
  const fetchCount = 6;
  const res = await fetchRecFeeds({
    dataSource: growthTestData.value?.dataSource || 0,
    feedCount: fetchCount,
    attachInfo: attachInfo.value,
    playStatus: 1, // 0: 未播放，1: 播放中
  });
  loadingRecFeeds.value = false;
  const loadedCount = res?.recommendFeeds.length || 0; // 加载的 feed 数量
  // 没有加载到数据
  if (!res || !loadedCount) {
    loadingEnd.value = true;
    return;
  }
  const baseCount = feedsList.value.length;
  const allFeeds = feedsList.value.concat(res.recommendFeeds.map((feed, index) => ({
    ...feed,
    index: baseCount + index,
  })) || []);

  store.commit(MutationTypes.SET_FEEDS_LIST, { value: allFeeds });
  attachInfo.value = res.attachInfo || '';
  return res;
};

const reportLeave = () => {
  const { collectionid: collectionId, dramaid: dramaId } = urlInfo.query;
  const pageLeaveTime = +new Date();
  const duration = pageEntryTime.value > 0 ? pageLeaveTime - pageEntryTime.value : 0;

  report('page.exp.end.exposure', {
    type: {
      play_status: isFullScreen.value ? 2 : 1,
      collection_id: collectionId || '',
      micro_drama_id: dramaId || '',
      duration,
    },
  });
};

const handlePageExpTime = () => {
  if (IS_SSR) {
    return;
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      console.log('页面被隐藏（切换到后台）');
      reportLeave();
    } else if (document.visibilityState === 'visible') {
      console.log('页面恢复可见（切回页面）');
      pageEntryTime.value = +new Date();
    }
  });

  window.addEventListener('beforeunload', () => {
    reportLeave();
  });
};

// 提供cookie相关信息
provideCookieInfo();
// 提供浏览器平台相关信息
provideBrowserInfo();
// 提供当前页面链接上的相关信息
provideUrlInfo();

// 初始化首页数据
const { initializeHomeData } = useInitializeHomeData();
// 初始化分享相关数据
const initializeShareData = useInitializeShareData();

watch(activeIndex, (val) => {
  isShowDramaReplay.value = false;

  if (!IS_SSR && swiper.value) {
    nextTick(() => {
      swiper.value.slideTo(val, 0);
      swiper.value.activeIndex = val;
      swiper.value?.update();
    });
  }
});

watch(feedsList, () => {
  nextTick(() => {
    console.log('feedsList update');
    swiper.value?.update();
  });
});

onMounted(async () => {
  await initializeHomeData();
  // 当页面数据初始化完成之后
  await initWXConf({ useWeishi: true });
  initializeShareData(); // 初始化分享
  const { collectionid: collectionId, dramaid: dramaId, debug } = urlInfo.query;

  // 初始化 swiper 实例可切换视频
  initSwiper();

  // 非合集和短剧，为普通视频，拉取推荐 feed 数据
  if (!collectionId && !dramaId) {
    getRecFeeds();
    initPagLib();
  }

  // 监听页面可见性变化，切换后台时上报时长
  handlePageExpTime();

  autoDownloadWSInWeibo();
  openWsInQQ();
  if (debug) {
    import('vconsole').then(({ default: VConsole }) => {
      new VConsole();
    });
  }
});
</script>

<style lang="scss">
.view-index {
  position: relative;
  display: flex;
  box-sizing: border-box;
  margin: 0 auto;
  height: 100%;
  color: #fff;
  overflow: hidden;
  flex-direction: column;

  .swiper-container {
    width: 100%;
    height: 100%;
  }

  * {
    box-sizing: inherit;
  }

  .hand-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99;
    background: rgba(0, 0, 0, 0.5);
    animation: delayedFade 5s forwards;

    .hand-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-37%, -50%);
      @include responsiveStyleSingle(width, 400, "px");
      @include responsiveStyleSingle(height, 456, "px");
    }
  }
}

.qrcode-wrap {
  position: absolute;
  @include responsiveStyleSingle(top, 20, "px");
  right: 50%;
  @include responsiveStyleSingle(margin-right, -515, "px");
  display: none;
}

.qrcode {
  @include responsiveStyleSingle(width, 120, "px");
  @include responsiveStyleSingle(height, 120, "px");
}

.qrcode-wrap .txt {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  @include responsiveStyleSingle(line-height, 25, "px");
  @include responsiveStyleSingle(margin-top, 9, "px");
  @include responsiveStyleSingle(font-size, 14, 'px');
}

@keyframes delayedFade {
  0% {
    opacity: 1;
  }
  80% {
    opacity: 1; /* 保持完全不透明 */
  }
  100% {
    opacity: 0;
    display: none;
  }
}
</style>
